<template>
  <div>
    <div id="section-to-print" v-if="showMainReport" class="report-container">
      <h1 class="report-title">Subject Report</h1>
      <p class="report-date">Generated on: {{ currentDate }}</p>

      <!-- Patient Details -->
      <section class="card">
        <header class="card-header">Subject Details</header>
        <div class="card-body">
          <div class="detail-row">
            <span class="detail-label">Subject ID:</span>
            <span>{{ patient.patient_identifier }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Name:</span>
            <span>
              {{ (patient.name ?? "").toString().trim() || "N/A" }}
              {{ patient.surname }}
            </span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Sex:</span>
            <span>{{ patient.sex }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Ethnicity:</span>
            <span>{{ patient.ethnicity }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Date of Birth:</span>
            <span>{{ patient.dob }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Age:</span>
            <span>{{ age }} years old</span>
          </div>
        </div>
      </section>

      <!-- Face Scan Details -->
      <section class="card">
        <header class="card-header">Face Scan 3D Snapshots</header>
        <div class="card-body">
          <div class="detail-row">
            <span class="detail-label">Scan Date:</span>
            <span>{{ scanDate }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Imaging Modality:</span>
            <span>{{ imagingModality }}</span>
          </div>
        </div>
      </section>
      <!-- 3D Snapshots -->
      <section v-if="snapshots.length" class="card">
        <div class="card-body snapshot-images">
          <!-- First row: Large images -->
          <div class="snapshot-row">
            <img
              v-for="(snapshot, index) in largeSnapshots"
              :key="'large-' + index"
              :src="snapshot"
              alt="3D Snapshot"
              class="snapshot-img-large"
            />
          </div>

          <!-- Second row -->
          <div class="snapshot-row">
            <img
              v-for="(snapshot, index) in smallSnapshots"
              :key="'small-' + index"
              :src="snapshot"
              alt="3D Snapshot"
              class="snapshot-img"
            />
          </div>
        </div>
      </section>

      <section v-if="pflAnalysisResult" class="card">
        <header class="card-header">PFL Analysis Results</header>
        <div class="card-body">
          <!-- PFL Results Table -->
          <table class="pfl-table">
            <tbody>
              <tr>
                <td class="bold-label">Subject PFL</td>
                <td>{{ pflAnalysisResult.PFL.toFixed(2) }} mm</td>
              </tr>
              <tr>
                <td class="bold-label">Z-score</td>
                <td>{{ pflAnalysisResult.PFLzScore?.toFixed(2) }}</td>
              </tr>
              <tr>
                <td class="bold-label">Percentile</td>
                <td>{{ pflAnalysisResult.PFLpercentile?.toFixed(2) }}%</td>
              </tr>
            </tbody>
          </table>

          <div :class="pfl - chart">
            <v-chart
              v-if="malePFLChartOptions && patient.sex === 'male'"
              :option="malePFLChartOptions"
              autoresize
              class="chart"
            />
            <v-chart
              v-if="femalePFLChartOptions && patient.sex === 'female'"
              :option="femalePFLChartOptions"
              autoresize
              class="chart"
            />
          </div>
        </div>
      </section>

      <!-- Classification Section -->
      <section
        v-if="classificationSnapshot"
        class="card classification-container"
      >
        <header class="card-header">Classification Results</header>
        <div class="card-body">
          <img
            :src="classificationSnapshot"
            alt="Classification Snapshot"
            class="classification-snapshot-img"
          />
        </div>
      </section>

      <!-- Mode1 Plot and Heatmap Analysis Section -->
      <section class="card mode1-heatmap-container">
        <!-- Heatmap -->
        <header v-if="heatmapSnapshot" class="card-header-Heatmap">
          Heatmap Visualization
        </header>
        <div v-if="heatmapSnapshot" class="card-body heatmap-container">
          <img
            :src="heatmapSnapshot"
            alt="Heatmap Snapshot"
            class="heatmap-snapshot-img"
          />
        </div>
        <header v-if="mode1PlotImage" class="card-header">
          Mode1 Plot Analysis
        </header>
        <table v-if="mode1PlotImage" class="mode1-table">
          <tbody>
            <tr>
              <td class="bold-label">Subject Age</td>
              <td>{{ age }}</td>
            </tr>
            <tr>
              <td class="bold-label">Subject Mode1</td>
              <td>
                {{
                  isNaN(Number(firstPrincipalComponent))
                    ? "N/A"
                    : Number(firstPrincipalComponent).toFixed(2)
                }}
              </td>
            </tr>
            <tr>
              <td class="bold-label">Percentile</td>
              <td>
                {{
                  isNaN(Number(mode1Percentile))
                    ? "N/A"
                    : Number(mode1Percentile).toFixed(2) + "%"
                }}
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="mode1PlotImage" class="card-body">
          <img :src="mode1PlotImage" alt="Mode1 Plot" class="mode1-plot-img" />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    patient: Object,
    facescan_id: String,
    scanDate: String,
    imagingModality: String,
    pflAnalysisResult: Object,
    showMainReport: Boolean,
    snapshots: Array,
    mode1PlotImage: String,
    heatmapSnapshot: String,
    firstPrincipalComponent: Number,
    mode1Percentile: Number,
    classificationSnapshot: String,
    malePFLChartOptions: Object,
    femalePFLChartOptions: Object,
  },
  data() {
    return {
      age: null,
      currentDate: "",
    };
  },
  computed: {
    largeSnapshots() {
      return this.snapshots.slice(0, 3);
    },
    smallSnapshots() {
      return this.snapshots.slice(3);
    },
  },
  methods: {
    setCurrentDate() {
      const now = new Date();
      this.currentDate = now.toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });
    },

    async loadAge() {
      this.age = await this.calculateAge();
    },
    async calculateAge() {
      const fasdBackendURL =
        localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
      const token = localStorage.getItem("userJWtoken");
      const response = await fetch(
        `${fasdBackendURL}/api/facescans/patientAge/${this.facescan_id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const age = await response.json();
      const PatientAge = parseFloat(age).toFixed(1);
      return PatientAge;
    },
  },
  mounted() {
    this.setCurrentDate();
    this.loadAge();
  },
};
</script>

<style scoped>
body {
  font-family: "Roboto", sans-serif;
  background-color: #f4f6f8;
}
.report-container {
  background-color: #ffffff;
  border-radius: 12px;
  padding: 30px;
  max-width: 1000px;
  margin: 20px auto;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  color: #333;
  width: 100%;
}
.report-title {
  font-size: 28px;
  font-weight: 700;
  text-align: center;
  color: #093667;
  margin-bottom: 30px;
}
.card {
  background-color: #ffffff;
  border-radius: 10px;
  margin-bottom: 20px;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.05);
  width: 100%;
}
.card-header {
  font-size: 18px;
  font-weight: 600;
  color: #1a497c;
  background-color: transparent;
  padding: 15px;
  border-bottom: 1px solid #e0e0e0;
  text-align: left;
}
.card-header-Heatmap {
  font-size: 18px;
  font-weight: 600;
  color: #1a497c;
  background-color: transparent;
  padding: 15px;
  border-bottom: 1px solid #e0e0e0;
  text-align: left;
}
.snapshot-images {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.snapshot-images {
  justify-content: space-evenly;
  flex-wrap: wrap;
}
.snapshot-row {
  display: flex;
  justify-content: space-evenly;
  margin-top: 10px;
}
.snapshot-img-large {
  width: 200px;
  height: 150px;
  object-fit: cover;
  margin: 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
}
.card-body {
  padding: 15px;
  text-align: left;
  margin-top: -15px;
}

.classification-container {
  text-align: center;
}
.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f1f1f1;
}

.detail-label {
  font-weight: 500;
  color: #555;
}
.snapshot-img {
  width: 180px;
  height: 90px;
  object-fit: cover;
  margin: 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
}
.pfl-chart {
  margin-top: 50px;
}

.custom-table thead th {
  background-color: #04408b !important;
  color: white;
}
.custom-table tbody tr td {
  border-top: none !important;
}
.custom-table tbody tr {
  border-bottom: none !important;
}
.custom-table {
  font-family: "Arial";
  font-size: 1rem;
}

.report-pfl-chart {
  width: 100%;
  text-align: left;
  margin-top: 20px;
}
.heatmap-container {
  display: flex;
  align-items: center;
  justify-content: space-around;
  margin-left: 20px;
  margin-bottom: 20px;
}

.heatmap-snapshot-img {
  width: 60%;
  max-width: 550px;
  height: 500px;
  border-radius: 8px;
}

.mode1-card {
  background-color: #ffffff;
  border-radius: 8px;
  margin: 10px auto;
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.mode1-table {
  width: 80%;
  margin: 0 auto;
  font-size: 0.9rem;
}
.mode1-label {
  font-weight: bold;
  color: #333;
}

.mode1-plot-img {
  width: 90%;
  height: 500px;
  display: block;
  margin-left: 40px;
  margin-top: 20px;
}
.classification-container {
  text-align: center;
  margin-top: 20px;
}
.classification-snapshot-img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.mode1-heatmap-container {
  text-align: center;
  margin-bottom: 20px;
  text-align: center;
}
.mode1-table {
  width: 50%;
  margin: 10px auto;
  font-size: 0.9rem;
  border: 2px solid #004ead;
  border-radius: 8px;
}
.mode1-table td {
  padding: 8px;
  border: 1px solid #004ead;
  text-align: left;
}
.mode1-table .bold-label {
  font-weight: bold;
}
.heatmap-container {
  display: flex;
  align-items: center;
  justify-content: center;
}
.pfl-table {
  width: 50%;
  margin: 40px auto;
  font-size: 0.9rem;
  border: 2px solid #004ead;
  border-radius: 8px;
}
.pfl-table td {
  padding: 8px;
  border: 1px solid #004ead;
  text-align: left;
}
.pfl-table .bold-label {
  font-weight: bold;
}
.bold-label {
  font-weight: bold;
}
.report-date {
  font-size: 14px;
  font-weight: 400;
  color: #666;
  text-align: center;
  margin-bottom: 20px;
}
</style>
